import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  memo,
  useRef,
} from 'react';
import { ListView } from '../ListView/ListView';
import { CategoryData, ListGridViewProps } from './ListGridView.types';
import { ItemProps } from '../ListView/ListView.types';

/**
 * ListGridView Component
 *
 * A grid-based component that displays items in rows and columns.
 * Supports row-specific item widths through the CategoryData.itemWidth property.
 *
 * @example
 * // Example with different item widths per row
 * const data = [
 *   { id: 1, name: 'Large Items', list: largeItems, itemWidth: 30 },
 *   { id: 2, name: 'Small Items', list: smallItems, itemWidth: 15 },
 * ];
 *
 * <ListGridView
 *   data={data}
 *   rowsCount={data.length}
 *   visibleRowsCount={2}
 *   isActive={true}
 *   onActiveChange={(rowIndex, colIndex) => console.log(rowIndex, colIndex)}
 * />
 */
export const ListGridView = memo(
  <T,>({
    rowsCount,
    visibleRowsCount,
    rowGap = 1,
    data,
    withTitle = false,
    isActive,
    initialRowActiveIndex,
    onFirstRow,
    onUp,
    onDown,
    onActiveChange,
    ...listViewProps
  }: ListGridViewProps<T>) => {
    const [currentRow, setCurrentRow] = useState(initialRowActiveIndex || 0);
    const [currentCol, setCurrentCol] = useState(0);

    // Track last reported position to prevent duplicate notifications
    const lastReportedPosition = useRef({
      row: initialRowActiveIndex || 0,
      col: 0,
    });

    // Debounce timer for notifications
    const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Single notification function to prevent duplicates
    const notifyPositionChange = useCallback(
      (rowIndex: number, colIndex: number) => {
        // Skip if not active
        if (!isActive) return;

        // If position hasn't changed, don't notify
        if (
          lastReportedPosition.current.row === rowIndex &&
          lastReportedPosition.current.col === colIndex
        ) {
          return;
        }

        // Clear any pending notifications
        if (notificationTimerRef.current) {
          clearTimeout(notificationTimerRef.current);
        }

        // Set a small delay to debounce multiple calls
        notificationTimerRef.current = setTimeout(() => {
          // Update last reported position
          lastReportedPosition.current = { row: rowIndex, col: colIndex };

          // Call both callbacks
          onActiveChange?.(rowIndex, colIndex);

          if (listViewProps.onIndexChange) {
            (listViewProps.onIndexChange as any)?.(rowIndex, colIndex);
          }

          notificationTimerRef.current = null;
        }, 50); // Short delay to debounce
      },
      [isActive, onActiveChange, listViewProps.onIndexChange]
    );

    // Validate and memoize the data
    const currentList = useMemo(() => {
      if (!Array.isArray(data)) {
        return [];
      }
      // Validate that each item has a list property
      const validData = data.every((item) => Array.isArray(item?.list));
      if (!validData) {
        return [];
      }
      return data;
    }, [data]);

    const itemsTotal = currentList.length;

    // Reset current row if it's out of bounds
    useEffect(() => {
      if (currentRow >= itemsTotal) {
        setCurrentRow(Math.max(0, itemsTotal - 1));
      }
    }, [itemsTotal, currentRow]);

    const getRowStyle = useCallback(
      (index: number): React.CSSProperties => {
        // Get row-specific data
        const rowData = currentList[index];
        // Get row-specific width or fallback to default
        const rowWidth = rowData?.itemWidth || listViewProps.itemWidth || 20;

        return {
          position: 'absolute',
          width: `${rowWidth}rem`,
          height: `${listViewProps.itemHeight}rem`,
          top: `${index * (listViewProps.itemHeight + rowGap)}rem`,
        };
      },
      [listViewProps.itemWidth, listViewProps.itemHeight, rowGap, currentList]
    );

    // Simple handler for column changes
    const handleColChange = useCallback(
      (rowIndex: number, colIndex: number) => {
        // Only update if this is coming from the active row
        if (rowIndex === currentRow) {
          setCurrentCol(colIndex);

          // Notify parent when column changes in active row
          notifyPositionChange(rowIndex, colIndex);
        }
      },
      [currentRow, notifyPositionChange]
    );

    // Simple handler for row changes
    const handleRowChange = useCallback(
      (index: number) => {
        // Don't update if row hasn't changed
        if (index === currentRow) return;

        // Update row index
        setCurrentRow(index);

        // Only reset column when explicitly navigating between rows
        setCurrentCol(0);

        // Notify parent when row changes
        notifyPositionChange(index, 0);
      },
      [currentRow, notifyPositionChange]
    );

    const renderRowItems = useCallback(
      ({ item, index, isActive }: ItemProps & { item: CategoryData<T> }) => {
        if (!item?.list) {
          return null;
        }

        // Get the item width for this specific row, or use the default
        const rowItemWidth = item.itemWidth || listViewProps.itemWidth || 20;

        return (
          <div key={index} style={getRowStyle(index)}>
            {withTitle && item.name && (
              <div className="ino-list-title-wrapper">
                <h3 className="ino-list-title">{item.name}</h3>
              </div>
            )}
            <ListView
              {...listViewProps}
              data={item.list}
              id={`${index}-list-view`}
              uniqueKey={`${index}-list-view`}
              listType="horizontal"
              itemsTotal={item.list.length}
              itemsCount={listViewProps.itemsCount || 1}
              buffer={listViewProps.buffer || 3}
              debounce={listViewProps.debounce || 200}
              itemWidth={rowItemWidth} // Use row-specific item width
              initialActiveIndex={
                listViewProps.initialActiveIndex !== undefined
                  ? Math.min(
                      listViewProps.initialActiveIndex,
                      item.list.length - 1
                    )
                  : 0
              }
              itemHeight={listViewProps.itemHeight || 30}
              gap={listViewProps.gap || 0}
              rowGap={rowGap || 0}
              withTitle={withTitle}
              isActive={isActive && currentRow === index}
              renderItem={(props) =>
                listViewProps.renderItem({ ...props, row: index })
              }
              nativeControle={true}
              onIndexChange={(colIndex) => {
                handleColChange(index, colIndex);
              }}
            />
          </div>
        );
      },
      [
        getRowStyle,
        listViewProps,
        withTitle,
        currentRow,
        rowGap,
        handleColChange,
      ]
    );

    // Handle isActive changes
    useEffect(() => {
      // Ensure we notify parent of current position when component becomes active
      notifyPositionChange(currentRow, currentCol);
    }, [currentRow, currentCol, notifyPositionChange]);

    // Cleanup the timer when component unmounts
    useEffect(() => {
      return () => {
        if (notificationTimerRef.current) {
          clearTimeout(notificationTimerRef.current);
        }
      };
    }, []);

    return (
      <ListView
        id="list-grid-view"
        uniqueKey="list-grid-view"
        itemWidth={listViewProps.itemWidth || 20}
        itemHeight={listViewProps.itemHeight || 30}
        data={currentList}
        listType="vertical"
        itemsCount={visibleRowsCount || 1}
        itemsTotal={itemsTotal}
        nativeControle={true}
        isActive={isActive}
        initialActiveIndex={initialRowActiveIndex || 0}
        debounce={listViewProps.debounce || 200}
        buffer={listViewProps.buffer || 0}
        gap={listViewProps.gap}
        rowGap={rowGap}
        onIndexChange={(index) => {
          handleRowChange(index);
        }}
        onDown={() => {
          onDown?.();
        }}
        onBack={() => {
          if (currentRow === 0) {
            onFirstRow?.({ key: 'back' });
          }
        }}
        onUp={() => {
          if (currentRow === 0) {
            onFirstRow?.({ key: 'up' });
          } else {
            onUp?.();
          }
        }}
        renderItem={({ index, item, style }) => {
          return renderRowItems({
            key: index,
            item,
            style,
            isActive: isActive,
            onMouseEnter: () => handleRowChange(index),
            index,
          });
        }}
      />
    );
  }
);
